import {
  ArchiveIcon,
  ExclamationTriangleIcon,
  GlobeIcon,
  MoonIcon,
  Pencil2Icon,
  PersonIcon,
  PlusCircledIcon,
  SunIcon,
} from "@radix-ui/react-icons";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useGetTheme from "src/lib/hooks/useGetTheme";
import { setTheme, themes } from "src/lib/slices/userSlice";
import SyncDrawer from "./SyncDrawer";
import UserSettings from "./UserSettings";
import { Button } from "./ui/button";
import { AlcDrawer, DrawerTitle } from "./ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import ReportBugDrawer from "./reportBugDrawer";
import { usePostHog } from "posthog-js/react";

function MenuItem({
  label,
  icon,
  href,
  setOpened,
  id,
}: {
  label: string;
  icon: React.ReactNode;
  href?: string;
  setOpened?: (opened: boolean) => void;
  id?: string;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Button
      variant={pathname === href ? "secondary" : "outline"}
      size="lg"
      style={{
        width: "100%",
        justifyContent: "flex-start",
        gap: "1rem",
        marginBottom: "0.5rem",
        minHeight: "2.5rem",
      }}
      onClick={() => {
        if (href) navigate(href);
        if (setOpened) setOpened(false);
      }}
      id={id}
    >
      {icon}
      {label}
    </Button>
  );
}

export default function MenuDrawer({ children }) {
  const [opened, setOpened] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const theme = useGetTheme();
  const postHog = usePostHog();

  return (
    <AlcDrawer
      isOpen={opened}
      setIsOpen={setOpened}
      asChild
      trigger={children}
      header={<DrawerTitle style={{ textAlign: "center" }}>Menu</DrawerTitle>}
    >
      <MenuItem
        setOpened={setOpened}
        label="Kalkulačka"
        icon={<Pencil2Icon />}
        href="/"
      />
      <MenuItem
        label="Seance"
        icon={<ArchiveIcon />}
        setOpened={setOpened}
        href="/sessions"
      />
      <h3
        style={{
          textAlign: "center",
          marginTop: "1rem",
          marginBottom: "0.25rem",
          fontSize: "0.75rem",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        Social
      </h3>
      <MenuItem
        setOpened={setOpened}
        label="Přátelé"
        icon={<PersonIcon />}
        href="/friends"
      />
      {/* <MenuItem
        setOpened={setOpened}
        label="Kalby"
        icon={<TargetIcon />}
        href="/parties"
      /> */}
      <MenuItem
        setOpened={setOpened}
        label="Uživatelé poblíž"
        icon={<GlobeIcon />}
        href="/nearby"
      />
      <h3
        style={{
          textAlign: "center",
          marginTop: "1rem",
          marginBottom: "0.25rem",
          fontSize: "0.75rem",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        Nastavení
      </h3>
      <UserSettings asChild>
        <div>
          <MenuItem label="Profil" icon={<PersonIcon />} />
        </div>
      </UserSettings>
      <MenuItem
        setOpened={setOpened}
        label="Vlastní nápoje"
        icon={<PlusCircledIcon />}
        href="/own-drinks"
      />
      <SyncDrawer>
        <div>
          <MenuItem
            label="Ukládání na cloud"
            icon={
              <img
                src="/images/cloud-icon.svg"
                alt=""
                style={{
                  filter: theme === "dark" ? "invert(1)" : undefined,
                  width: "15px",
                }}
              />
            }
          />
        </div>
      </SyncDrawer>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="lg"
            style={{
              width: "100%",
              justifyContent: "flex-start",
              gap: "1rem",
              marginBottom: "0.5rem",
              minHeight: "2.5rem",
              position: "relative",
            }}
          >
            <SunIcon className="rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <MoonIcon className="absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontSize: "0.5rem",
                  color: "hsl(var(--muted-foreground))",
                  lineHeight: "0.25rem",
                }}
              >
                Tmavý režim
              </div>
              <div>
                {(theme ?? user?.theme) === themes.Light && "Světlý"}
                {(theme ?? user?.theme) === themes.Dark && "Tmavý"}
                {(theme ?? user?.theme) === themes.System && "Podle systému"}
              </div>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => dispatch(setTheme(themes.Light))}>
            Světlý
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch(setTheme(themes.Dark))}>
            Tmavý
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => dispatch(setTheme(themes.System))}>
            Podle systému
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {!postHog.has_opted_out_capturing() && (
        <>
          <h3
            style={{
              textAlign: "center",
              marginTop: "1rem",
              marginBottom: "0.25rem",
              fontSize: "0.75rem",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Zpětná vazba
          </h3>
          <ReportBugDrawer>
            <div>
              <MenuItem
                label="Nahlásit chybu"
                icon={<ExclamationTriangleIcon />}
                id="posthog-report-bug"
              />
            </div>
          </ReportBugDrawer>
        </>
      )}
    </AlcDrawer>
  );
}
